import { useCallback, useEffect, useState } from 'react'
import { NeedsPassphrase, readLinkFile } from './file'
import { fetchLocalPayload, LockedOut, probeLocalHost, WrongCode } from './localHost'
import { type LinkPayload } from './payload'

export type LinkStatus =
  | { kind: 'idle' }
  | { kind: 'preparing' }
  | { kind: 'awaiting-code' }
  | { kind: 'loaded'; payload: LinkPayload; via: 'local' | 'file' }
  | { kind: 'error'; message: string; retryable: boolean }

/**
 * The whole link, as one hook.
 *
 * Two doors, and both end in the same `loaded` status carrying the same payload, so no panel below
 * this ever learns which one the data came through.
 *
 * **Nothing is persisted.** No localStorage, no sessionStorage, no IndexedDB. Closing the tab is
 * the delete button, and the absence of a "stay signed in" option here is the feature.
 */
export function useLink() {
  const [status, setStatus] = useState<LinkStatus>({ kind: 'idle' })
  // Null until the probe answers. Undefined-vs-false matters: the code field must not flash on a
  // page that turns out to be covellah.com.
  const [servedByPhone, setServedByPhone] = useState<boolean | null>(null)

  // Asked once, on arrival. On covellah.com the route does not exist and this settles to false in
  // a single failed request.
  useEffect(() => {
    const controller = new AbortController()
    probeLocalHost(controller.signal).then((result) => {
      setServedByPhone(result !== null)
      if (result !== null) setStatus({ kind: 'awaiting-code' })
    })
    return () => controller.abort()
  }, [])

  const reset = useCallback(() => {
    setStatus(servedByPhone ? { kind: 'awaiting-code' } : { kind: 'idle' })
  }, [servedByPhone])

  /** Trades the six digits on the phone's screen for the payload. */
  const submitCode = useCallback(async (code: string) => {
    setStatus({ kind: 'preparing' })
    try {
      const payload = await fetchLocalPayload(code)
      setStatus({ kind: 'loaded', payload, via: 'local' })
    } catch (error) {
      if (error instanceof WrongCode) {
        setStatus({ kind: 'error', message: error.message, retryable: true })
        return
      }
      if (error instanceof LockedOut) {
        setStatus({ kind: 'error', message: error.message, retryable: false })
        return
      }
      setStatus({
        kind: 'error',
        message: error instanceof Error ? error.message : 'Your phone did not answer.',
        retryable: true,
      })
    }
  }, [])

  const loadFile = useCallback(async (file: File, passphrase?: string) => {
    setStatus({ kind: 'preparing' })
    try {
      const payload = await readLinkFile(file, passphrase)
      setStatus({ kind: 'loaded', payload, via: 'file' })
    } catch (error) {
      if (error instanceof NeedsPassphrase) {
        setStatus({ kind: 'error', message: error.message, retryable: true })
        return
      }
      setStatus({
        kind: 'error',
        message: error instanceof Error ? error.message : 'That file could not be read.',
        retryable: true,
      })
    }
  }, [])

  return { status, servedByPhone, submitCode, loadFile, reset }
}
